import React, { useState, useEffect } from 'react'
import {
  X,
  HelpCircle,
  Eye,
  Edit3,
  UserCheck,
  Search,
  Check,
  CheckCircle2,
  Tag as TagIcon,
  Sparkles,
  GraduationCap,
} from 'lucide-react'
import { colors, radii, typography } from '../../styles/tokens'
import { qaApi, QA_CATEGORIES, type QACategory } from '../../api/qa'
import { alumniApi, type AlumniProfileItem } from '../../api/alumni'
import { useAuthStore } from '../../stores/authStore'
import { Avatar } from '../shared/Avatar'
import { VerifiedBadge } from '../shared/VerifiedBadge'
import toast from 'react-hot-toast'

interface AskQuestionModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (newQuestionId: string) => void
  initialTargetAlumnus?: AlumniProfileItem | null
}

const POPULAR_TAGS = [
  'interview-prep',
  'internship',
  'resume-review',
  'career-switch',
  'faang',
  'higher-studies',
  'system-design',
  'off-campus',
  'startups',
  'data-science',
]

export function AskQuestionModal({
  isOpen,
  onClose,
  onSuccess,
  initialTargetAlumnus,
}: AskQuestionModalProps) {
  const currentUser = useAuthStore((s) => s.user)
  const [tab, setTab] = useState<'write' | 'preview'>('write')

  // Form fields
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<QACategory>(QA_CATEGORIES[0])
  const [tagInput, setTagInput] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [collegeContext, setCollegeContext] = useState(currentUser?.college_name || '')
  const [courseContext, setCourseContext] = useState(currentUser?.course || '')

  // Target alumni tagging
  const [availableAlumni, setAvailableAlumni] = useState<AlumniProfileItem[]>([])
  const [selectedAlumniIds, setSelectedAlumniIds] = useState<string[]>([])
  const [alumniSearch, setAlumniSearch] = useState('')
  const [loadingAlumni, setLoadingAlumni] = useState(false)

  // Validation & Submission
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submittedQuestionId, setSubmittedQuestionId] = useState<string | null>(null)

  // Fetch alumni for picker
  useEffect(() => {
    if (!isOpen) return
    let active = true
    setLoadingAlumni(true)
    alumniApi
      .getAll({ limit: 50 })
      .then((res) => {
        if (!active) return
        setAvailableAlumni(res.data.data || [])
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoadingAlumni(false)
      })
    return () => {
      active = false
    }
  }, [isOpen])

  // Handle initial alumnus
  useEffect(() => {
    if (initialTargetAlumnus) {
      setSelectedAlumniIds((prev) =>
        prev.includes(initialTargetAlumnus.user_id) ? prev : [...prev, initialTargetAlumnus.user_id]
      )
    }
  }, [initialTargetAlumnus])

  if (!isOpen) return null

  const handleAddTag = (tagToAdd: string) => {
    const cleaned = tagToAdd.trim().replace(/^#/, '').toLowerCase()
    if (!cleaned || tags.includes(cleaned)) return
    if (tags.length >= 8) {
      toast.error('Maximum 8 tags allowed')
      return
    }
    setTags([...tags, cleaned])
    setTagInput('')
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove))
  }

  const toggleAlumnusSelection = (alumnusId: string) => {
    setSelectedAlumniIds((prev) =>
      prev.includes(alumnusId) ? prev.filter((id) => id !== alumnusId) : [...prev, alumnusId]
    )
  }

  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    if (title.trim().length < 10) {
      errs.title = 'Title must be at least 10 characters.'
    } else if (title.trim().length > 200) {
      errs.title = 'Title cannot exceed 200 characters.'
    }

    if (description.trim().length < 20) {
      errs.description = 'Please provide more details (at least 20 characters).'
    } else if (description.trim().length > 5000) {
      errs.description = 'Description cannot exceed 5000 characters.'
    }

    if (!category) {
      errs.category = 'Please choose a category.'
    }

    if (tags.length === 0) {
      errs.tags = 'Please add at least 1 tag to help categorize your question.'
    }

    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    if (submitting) return

    setSubmitting(true)
    try {
      const res = await qaApi.createQuestion({
        title: title.trim(),
        description: description.trim(),
        category,
        tags,
        target_alumni_ids: selectedAlumniIds,
        college_context: collegeContext.trim(),
        course_context: courseContext.trim(),
      })

      const newQ = res.data.data
      setSubmittedQuestionId(newQ.id)
      toast.success('Your question has been published to the Alumni Q&A feed!')
      setTimeout(() => {
        onSuccess(newQ.id)
        onClose()
      }, 1200)
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to submit question. Please try again.'
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const filteredAlumni = availableAlumni.filter((a) => {
    if (!alumniSearch.trim()) return true
    const term = alumniSearch.toLowerCase()
    return (
      a.full_name.toLowerCase().includes(term) ||
      a.company.toLowerCase().includes(term) ||
      a.job_title.toLowerCase().includes(term) ||
      a.skills.some((s) => s.toLowerCase().includes(term)) ||
      a.expertise.some((e) => e.toLowerCase().includes(term))
    )
  })

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 210,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 680,
          maxHeight: '90vh',
          background: 'var(--color-bg)',
          borderRadius: 24,
          border: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-float)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'navRise 200ms ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 22px',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: radii.md,
                background: '#111114',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HelpCircle size={18} />
            </div>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: '17px',
                  fontWeight: 800,
                  color: 'var(--color-text-primary)',
                  fontFamily: typography.fontDisplay,
                }}
              >
                Ask Alumni a Question
              </h2>
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                Get authoritative career and placement guidance from verified alumni
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                display: 'flex',
                background: 'var(--color-surface)',
                borderRadius: radii.pill,
                border: '1px solid var(--color-border)',
                padding: '2px',
              }}
            >
              <button
                type="button"
                onClick={() => setTab('write')}
                style={{
                  padding: '5px 12px',
                  borderRadius: radii.pill,
                  border: 'none',
                  background: tab === 'write' ? '#111114' : 'transparent',
                  color: tab === 'write' ? '#fff' : 'var(--color-text-secondary)',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Edit3 size={12} /> Write
              </button>
              <button
                type="button"
                onClick={() => setTab('preview')}
                style={{
                  padding: '5px 12px',
                  borderRadius: radii.pill,
                  border: 'none',
                  background: tab === 'preview' ? '#111114' : 'transparent',
                  color: tab === 'preview' ? '#fff' : 'var(--color-text-secondary)',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Eye size={12} /> Preview
              </button>
            </div>

            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
                padding: '6px',
                borderRadius: '50%',
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Scrollable Form Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '22px' }}>
          {submittedQuestionId ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '14px',
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: 'rgba(34,197,94,0.15)',
                  color: '#22C55E',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircle2 size={36} />
              </div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>Question Submitted!</h3>
              <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: '14px', maxWidth: 360 }}>
                Your question is now published. Tagged alumni have been notified. Redirecting...
              </p>
            </div>
          ) : tab === 'preview' ? (
            /* Live Preview Mode */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: radii.md,
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  fontSize: '12.5px',
                  color: 'var(--color-text-secondary)',
                }}
              >
                Previewing how your question will appear to alumni and students:
              </div>

              <div
                style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: 18,
                  padding: '18px',
                  background: 'var(--color-surface)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '3px 9px',
                      borderRadius: radii.pill,
                      background: 'var(--color-bg)',
                      border: '1px solid var(--color-border)',
                    }}
                  >
                    {category}
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      color: 'var(--color-text-muted)',
                      border: '1px solid var(--color-border)',
                      padding: '2px 8px',
                      borderRadius: radii.pill,
                    }}
                  >
                    Needs Answer
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Avatar
                    src={currentUser?.avatar_url}
                    name={currentUser?.full_name || 'Student'}
                    size={36}
                  />
                  <div>
                    <div style={{ fontSize: '13.5px', fontWeight: 600 }}>
                      {currentUser?.full_name || 'Student'}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                      {[collegeContext, courseContext].filter(Boolean).join(' · ') || 'Campus Student'} · Just now
                    </div>
                  </div>
                </div>

                <h3
                  style={{
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: 700,
                    color: 'var(--color-text-primary)',
                    fontFamily: typography.fontDisplay,
                  }}
                >
                  {title || 'Question title will appear here'}
                </h3>

                <p
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    lineHeight: 1.6,
                    color: 'var(--color-text-secondary)',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {description || 'Detailed description will appear here...'}
                </p>

                {tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {tags.map((t) => (
                      <span
                        key={t}
                        style={{
                          fontSize: '11px',
                          color: 'var(--color-text-secondary)',
                          background: 'var(--color-bg)',
                          border: '1px solid var(--color-border)',
                          padding: '2px 8px',
                          borderRadius: '6px',
                        }}
                      >
                        #{t}
                      </span>
                    ))}
                  </div>
                )}

                {selectedAlumniIds.length > 0 && (
                  <div
                    style={{
                      padding: '8px 12px',
                      borderRadius: radii.md,
                      background: 'var(--color-bg)',
                      border: '1px solid var(--color-border)',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontWeight: 600, color: 'var(--color-text-muted)' }}>Target Alumni:</span>
                    <span>{selectedAlumniIds.length} alumni will be tagged</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Write Mode Form */
            <form id="askQuestionForm" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {/* Question Title */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    Question Title <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <span style={{ fontSize: '11.5px', color: 'var(--color-text-muted)' }}>
                    {title.length}/200
                  </span>
                </div>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. How should I prepare for my first software engineering internship?"
                  maxLength={200}
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    borderRadius: radii.md,
                    border: '1px solid',
                    borderColor: errors.title ? '#EF4444' : 'var(--color-border)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    fontSize: '14px',
                    outline: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
                {errors.title && (
                  <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px', display: 'block' }}>
                    {errors.title}
                  </span>
                )}
              </div>

              {/* Category */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '6px' }}>
                  Category <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as QACategory)}
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    borderRadius: radii.md,
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    fontSize: '13.5px',
                    outline: 'none',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                >
                  {QA_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Detailed Question Description */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    Detailed Question Description <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <span style={{ fontSize: '11.5px', color: 'var(--color-text-muted)' }}>
                    {description.length}/5000
                  </span>
                </div>
                <textarea
                  rows={6}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Provide background: What is your current situation? What have you tried so far? What specific advice or roadmaps are you looking for from alumni?"
                  maxLength={5000}
                  style={{
                    width: '100%',
                    padding: '11px 14px',
                    borderRadius: radii.md,
                    border: '1px solid',
                    borderColor: errors.description ? '#EF4444' : 'var(--color-border)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    fontSize: '13.5px',
                    lineHeight: 1.55,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                {errors.description && (
                  <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px', display: 'block' }}>
                    {errors.description}
                  </span>
                )}
              </div>

              {/* Tags */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '6px' }}>
                  Tags <span style={{ color: '#EF4444' }}>*</span> (add keywords like #internship, #interview-prep)
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddTag(tagInput)
                      }
                    }}
                    placeholder="Type a tag and press Enter"
                    style={{
                      flex: 1,
                      padding: '9px 12px',
                      borderRadius: radii.md,
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleAddTag(tagInput)}
                    style={{
                      padding: '0 16px',
                      borderRadius: radii.md,
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Add
                  </button>
                </div>

                {/* Popular tags suggestions */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', alignSelf: 'center' }}>
                    Popular:
                  </span>
                  {POPULAR_TAGS.map((pt) => (
                    <button
                      key={pt}
                      type="button"
                      onClick={() => handleAddTag(pt)}
                      disabled={tags.includes(pt)}
                      style={{
                        padding: '2px 8px',
                        borderRadius: radii.pill,
                        border: '1px solid var(--color-border)',
                        background: tags.includes(pt) ? 'var(--color-surface)' : 'transparent',
                        color: tags.includes(pt) ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
                        fontSize: '11px',
                        cursor: tags.includes(pt) ? 'default' : 'pointer',
                      }}
                    >
                      +{pt}
                    </button>
                  ))}
                </div>

                {/* Selected tags */}
                {tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                    {tags.map((t) => (
                      <span
                        key={t}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '4px 10px',
                          borderRadius: radii.pill,
                          background: '#111114',
                          color: '#FFFFFF',
                          fontSize: '11.5px',
                          fontWeight: 600,
                        }}
                      >
                        #{t}
                        <X
                          size={12}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRemoveTag(t)}
                        />
                      </span>
                    ))}
                  </div>
                )}
                {errors.tags && (
                  <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '4px', display: 'block' }}>
                    {errors.tags}
                  </span>
                )}
              </div>

              {/* Tag Specific Alumni (Optional) */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '4px' }}>
                  Tag Specific Alumni (Optional)
                </label>
                <p style={{ margin: '0 0 8px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                  Tagging alumni will notify them directly and surface your question on their dashboard.
                </p>

                {/* Search Alumni in modal */}
                <div style={{ position: 'relative', marginBottom: '8px' }}>
                  <Search
                    size={14}
                    style={{ position: 'absolute', left: '12px', top: '11px', color: 'var(--color-text-muted)' }}
                  />
                  <input
                    type="text"
                    value={alumniSearch}
                    onChange={(e) => setAlumniSearch(e.target.value)}
                    placeholder="Search alumni by name, company, or expertise..."
                    style={{
                      width: '100%',
                      padding: '9px 12px 9px 34px',
                      borderRadius: radii.md,
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                      fontSize: '12.5px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div
                  style={{
                    maxHeight: 180,
                    overflowY: 'auto',
                    border: '1px solid var(--color-border)',
                    borderRadius: radii.md,
                    padding: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    background: 'var(--color-bg)',
                  }}
                >
                  {filteredAlumni.slice(0, 15).map((alm) => {
                    const isSelected = selectedAlumniIds.includes(alm.user_id)
                    return (
                      <div
                        key={alm.user_id}
                        onClick={() => toggleAlumnusSelection(alm.user_id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '7px 10px',
                          borderRadius: radii.md,
                          background: isSelected ? 'var(--color-surface)' : 'transparent',
                          border: isSelected ? '1px solid #111114' : '1px solid transparent',
                          cursor: 'pointer',
                          gap: '10px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                          <Avatar src={alm.avatar_url} name={alm.full_name} size={28} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                {alm.full_name}
                              </span>
                              {alm.is_verified === 1 && <VerifiedBadge size={12} />}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {alm.job_title} · {alm.company}
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            border: '1px solid',
                            borderColor: isSelected ? '#111114' : 'var(--color-border)',
                            background: isSelected ? '#111114' : 'transparent',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {isSelected && <Check size={12} />}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Context: College and Course (Optional) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                    College Context
                  </label>
                  <input
                    type="text"
                    value={collegeContext}
                    onChange={(e) => setCollegeContext(e.target.value)}
                    placeholder="e.g. Stanford University"
                    maxLength={100}
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: radii.md,
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                      fontSize: '12.5px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                    Course / Branch Context
                  </label>
                  <input
                    type="text"
                    value={courseContext}
                    onChange={(e) => setCourseContext(e.target.value)}
                    placeholder="e.g. B.Tech Computer Science"
                    maxLength={100}
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: radii.md,
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-surface)',
                      color: 'var(--color-text-primary)',
                      fontSize: '12.5px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '16px 22px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
            Campnect Alumni Network Guideline Compliant
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: '9px 18px',
                borderRadius: radii.pill,
                border: '1px solid var(--color-border)',
                background: 'transparent',
                color: 'var(--color-text-primary)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              form="askQuestionForm"
              disabled={submitting}
              onClick={tab === 'preview' ? handleSubmit : undefined}
              style={{
                padding: '9px 24px',
                borderRadius: radii.pill,
                border: 'none',
                background: '#111114',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: 700,
                fontFamily: typography.fontDisplay,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Publishing Question...' : 'Post Question'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
